import React, { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Alert, AlertDescription } from "./ui/alert";
import { CheckCircle, XCircle } from "lucide-react";

/**
 * Profile Completion Component
 * Used for OAuth users who need to select their user role
 *
 * This component should be shown to users where profile_completed === false
 */
const CompleteProfile = () => {
  const { user, updateProfile, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState("");
  const [phone, setPhone] = useState(user?.phone || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!userRole) {
      setError("Please select a role to continue");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const result = await updateProfile({
        full_name: user.full_name,
        phone: phone || null,
        user_role: userRole,
      });

      if (result.success) {
        // Profile completed successfully
        setSuccess(true);

        // Refresh user data from database to get updated profile_completed status
        await checkAuth();

        // Wait a moment to show success message, then navigate based on role
        setTimeout(() => {
          // Navigate based on user role
          if (userRole === 'admin') {
            navigate("/admin");
          } else if (userRole === 'investor') {
            navigate("/opportunities");
          } else {
            navigate("/search");
          }
        }, 1500);
      } else {
        setError(result.error || "Failed to complete profile");
      }
    } catch (err) {
      setError("An unexpected error occurred");
      console.error("Profile completion error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSkipForNow = () => {
    // Optional: allow users to skip and complete later
    navigate("/dashboard");
  };

  const roles = [
    {
      value: "homeowner",
      label: "Homeowner",
      description: "Looking to understand my property's value",
    },
    {
      value: "investor",
      label: "Investor",
      description: "Evaluating properties for investment",
    },
    // {
    //   value: "both",
    //   label: "Both",
    //   description: "I'm both a homeowner and investor",
    // },
  ];

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-3xl font-bold">
            Complete Your Profile
          </CardTitle>
          <CardDescription>
            Welcome, {user?.full_name}! Please select your role to continue.
          </CardDescription>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <Alert variant="destructive">
                <XCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {success && (
              <Alert className="border-green-500 bg-green-50 text-green-900">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription>
                  Profile completed successfully! Redirecting to home...
                </AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="phone" className="text-base font-medium">
                Phone Number{" "}
                <span className="text-sm text-muted-foreground font-normal">
                  (Optional)
                </span>
              </Label>
              <Input
                id="phone"
                type="tel"
                placeholder="+1 (555) 000-0000"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="h-11"
              />
              <p className="text-sm text-muted-foreground">
                We'll use this to contact you about your property
              </p>
            </div>

            <div className="space-y-4">
              <Label className="text-base font-medium">I am a:</Label>

              <RadioGroup
                value={userRole}
                onValueChange={setUserRole}
                className="space-y-3"
              >
                {roles.map((role) => (
                  <div
                    key={role.value}
                    className="flex items-center space-x-3 p-4 border rounded-lg hover:bg-accent transition-colors"
                  >
                    <RadioGroupItem value={role.value} id={role.value} />
                    <Label
                      htmlFor={role.value}
                      className="flex-1 cursor-pointer"
                    >
                      <div className="space-y-1">
                        <p className="font-medium leading-none">{role.label}</p>
                        <p className="text-sm text-muted-foreground">
                          {role.description}
                        </p>
                      </div>
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            <div className="flex flex-col space-y-3">
              <Button
                type="submit"
                disabled={loading || !userRole || success}
                className="w-full"
              >
                {success
                  ? "✓ Profile Completed!"
                  : loading
                  ? "Completing Profile..."
                  : "Complete Profile"}
              </Button>

              {/* Optional: Allow users to skip */}
              {/* 
              <Button
                type="button"
                variant="outline"
                onClick={handleSkipForNow}
                className="w-full"
              >
                Skip for Now
              </Button>
              */}
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default CompleteProfile;
